import React from 'react';
import { useTranslation } from 'react-i18next';
import { QuoteEditData } from '../../../../hooks/useDocumentEdit';

interface EditableQuoteExtendedFieldsProps {
  data: QuoteEditData;
  onUpdate: (field: keyof QuoteEditData, value: any) => void;
  accentColor?: string;
}

/**
 * Composant pour les champs étendus des devis
 * ✅ Conforme Prisma: status, signatureStatus, internalNotes, termsConditions, title, multi-devise
 */
export const EditableQuoteExtendedFields: React.FC<EditableQuoteExtendedFieldsProps> = ({
  data,
  onUpdate,
  accentColor = '#000000',
}) => {
  const { t } = useTranslation();
  return (
    <div className="quote-extended-fields">
      {/* ✅ WORKFLOW & STATUT */}
      <div className="extended-section">
        <h3 style={{ color: accentColor }}>{t('templates.extendedFields.statusAndWorkflow')}</h3>
        <div className="form-grid-2">
          <div className="form-field">
            <label>{t('templates.extendedFields.quoteStatus')}</label>
            <select
              value={data.status || 'DRAFT'}
              onChange={(e) => onUpdate('status', e.target.value)}
              style={{ borderColor: accentColor + '40' }}
            >
              <option value="DRAFT">{t('templates.extendedFields.statusDraft')}</option>
              <option value="SENT">{t('templates.extendedFields.statusSent')}</option>
              <option value="ACCEPTED">{t('templates.extendedFields.statusAccepted')}</option>
              <option value="REJECTED">{t('templates.extendedFields.statusRejected')}</option>
              <option value="EXPIRED">{t('templates.extendedFields.statusExpired')}</option>
            </select>
          </div>

          <div className="form-field">
            <label>{t('templates.extendedFields.signatureStatus')}</label>
            <select
              value={data.signatureStatus || 'UNSIGNED'}
              onChange={(e) => onUpdate('signatureStatus', e.target.value)}
              style={{ borderColor: accentColor + '40' }}
            >
              <option value="UNSIGNED">{t('templates.extendedFields.signatureUnsigned')}</option>
              <option value="SIGNED">{t('templates.extendedFields.signatureSigned')}</option>
              <option value="REFUSED">{t('templates.extendedFields.signatureRejected')}</option>
            </select>
          </div>

          {data.signatureStatus === 'SIGNED' && (
            <>
              <div className="form-field">
                <label>{t('templates.extendedFields.signatureDate')}</label>
                <input
                  type="datetime-local"
                  value={data.signedAt ? data.signedAt.slice(0, 16) : ''}
                  onChange={(e) => onUpdate('signedAt', e.target.value ? new Date(e.target.value).toISOString() : undefined)}
                  style={{ borderColor: accentColor + '40' }}
                />
              </div>

              <div className="form-field">
                <label>{t('templates.extendedFields.acceptedBy')}</label>
                <input
                  type="text"
                  value={data.acceptedBy || ''}
                  onChange={(e) => onUpdate('acceptedBy', e.target.value)}
                  placeholder={t('templates.extendedFields.acceptedByPlaceholder')}
                  style={{ borderColor: accentColor + '40' }}
                />
              </div>
            </>
          )}

          {data.signatureStatus === 'REFUSED' && (
            <div className="form-field full-width">
              <label>{t('templates.extendedFields.rejectionReason')}</label>
              <textarea
                value={data.rejectionReason || ''}
                onChange={(e) => onUpdate('rejectionReason', e.target.value)}
                rows={3}
                placeholder={t('templates.extendedFields.rejectionReasonPlaceholder')}
                style={{ borderColor: accentColor + '40' }}
              />
            </div>
          )}
        </div>
      </div>

      {/* ✅ CHAMPS MÉTIER */}
      <div className="extended-section">
        <h3 style={{ color: accentColor }}>{t('templates.extendedFields.additionalInfo')}</h3>

        <div className="form-field">
          <label>{t('templates.extendedFields.documentTitle')}</label>
          <input
            type="text"
            value={data.title || ''}
            onChange={(e) => onUpdate('title', e.target.value)}
            placeholder={t('templates.extendedFields.documentTitlePlaceholder')}
            style={{ borderColor: accentColor + '40' }}
          />
        </div>

        {/* S6 — Toggle Autoliquidation TVA BTP */}
        <div className="form-field" style={{ flexDirection: 'row', alignItems: 'center', gap: '0.75rem' }}>
          <input
            type="checkbox"
            id="isReverseChargeQuote"
            checked={data.isReverseCharge || false}
            onChange={(e) => onUpdate('isReverseCharge', e.target.checked)}
            style={{ width: 18, height: 18, accentColor }}
          />
          <label htmlFor="isReverseChargeQuote" style={{ marginBottom: 0, cursor: 'pointer' }}>
            {t('templates.extendedFields.reverseCharge')}
          </label>
        </div>
        {data.isReverseCharge && (
          <div style={{ background: '#fef3c7', borderRadius: 8, padding: '0.5rem 0.75rem', fontSize: '0.8rem', color: '#92400e', marginBottom: '1rem' }}>
            {t('templates.extendedFields.reverseChargeNotice')}
          </div>
        )}

        {/* Notes internes gérées par EditableNotesSection */}

        <div className="form-field">
          <label>{t('templates.extendedFields.termsConditions')}</label>
          <textarea
            value={data.termsConditions || ''}
            onChange={(e) => onUpdate('termsConditions', e.target.value)}
            rows={4}
            placeholder={t('templates.extendedFields.termsPlaceholder')}
            style={{ borderColor: accentColor + '40' }}
          />
        </div>
      </div>

      {/* ✅ MULTI-DEVISE */}
      <div className="extended-section">
        <h3 style={{ color: accentColor }}>{t('templates.extendedFields.multiCurrency')}</h3>
        <div className="form-grid-3">
          <div className="form-field">
            <label>{t('templates.extendedFields.quoteCurrency')}</label>
            <select
              value={data.currencyCode || 'EUR'}
              onChange={(e) => onUpdate('currencyCode', e.target.value)}
              style={{ borderColor: accentColor + '40' }}
            >
              <option value="EUR">EUR (€)</option>
              <option value="USD">USD ($)</option>
              <option value="GBP">GBP (£)</option>
              <option value="CHF">CHF</option>
              <option value="CAD">CAD</option>
            </select>
          </div>

          <div className="form-field">
            <label>{t('templates.extendedFields.baseCurrency')}</label>
            <select
              value={data.baseCurrencyCode || 'EUR'}
              onChange={(e) => onUpdate('baseCurrencyCode', e.target.value)}
              style={{ borderColor: accentColor + '40' }}
            >
              <option value="EUR">EUR (€)</option>
              <option value="USD">USD ($)</option>
              <option value="GBP">GBP (£)</option>
              <option value="CHF">CHF</option>
              <option value="CAD">CAD</option>
            </select>
          </div>

          <div className="form-field">
            <label>{t('templates.extendedFields.exchangeRate')}</label>
            <input
              type="number"
              step="0.000001"
              value={data.exchangeRate || 1}
              onChange={(e) => onUpdate('exchangeRate', parseFloat(e.target.value) || 1)}
              placeholder="1.0"
              style={{ borderColor: accentColor + '40' }}
            />
          </div>
        </div>

        {data.currencyCode !== 'EUR' && (
          <div className="currency-conversion-info" style={{
            backgroundColor: accentColor + '10',
            borderLeft: `3px solid ${accentColor}`,
            padding: '1rem',
            marginTop: '1rem',
            borderRadius: '4px'
          }}>
            <small style={{ color: '#495057' }}>
              <strong>{t('templates.extendedFields.autoConversion')}</strong><br />
              Total HT en {data.baseCurrencyCode || 'EUR'}: {data.baseTotalHT?.toFixed(2) || '0.00'}<br />
              Total TTC en {data.baseCurrencyCode || 'EUR'}: {data.baseTotalTTC?.toFixed(2) || '0.00'}
            </small>
          </div>
        )}
      </div>

      <style>{`
        .quote-extended-fields {
          margin-top: 2rem;
          padding-top: 2rem;
          border-top: 2px solid #e9ecef;
        }

        .extended-section {
          margin-bottom: 2rem;
        }

        .extended-section h3 {
          font-size: 1.1rem;
          font-weight: 600;
          margin-bottom: 1rem;
          padding-bottom: 0.5rem;
          border-bottom: 1px solid #e9ecef;
        }

        .form-grid-2 {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 1rem;
        }

        .form-grid-3 {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 1rem;
        }

        .form-field {
          display: flex;
          flex-direction: column;
        }

        .form-field.full-width {
          grid-column: 1 / -1;
        }

        .form-field label {
          font-size: 0.875rem;
          font-weight: 500;
          color: #495057;
          margin-bottom: 0.5rem;
        }

        .form-field input,
        .form-field select,
        .form-field textarea {
          padding: 0.625rem;
          border: 1px solid #ced4da;
          border-radius: 4px;
          font-size: 0.875rem;
          transition: border-color 0.2s;
        }

        .form-field input:focus,
        .form-field select:focus,
        .form-field textarea:focus {
          outline: none;
          border-color: currentColor;
          box-shadow: 0 0 0 3px currentColor10;
        }

        .form-field textarea {
          resize: vertical;
          font-family: inherit;
        }

        @media (max-width: 768px) {
          .form-grid-2,
          .form-grid-3 {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};
